import { AppReviews, Review } from '../interfaces/domain/reviews.modal';
import {
  AppCommentsResponseDto,
  AppRatingResponseDto,
} from '../interfaces/dtos/review.dto';
import { ILoginService } from '../interfaces/services/login-service.interface';
import { IReviewsService } from '../interfaces/services/reviews-service.interface';
import { DataFetchService } from './data-fetch.service';
import { ReviewsMapper } from './mappers/reviews-mapper';
import { PromiseAwaiter } from './promise-awaiter';
import { getOverwolfCurrentUser } from './overwolf/overwolf-store.service';
import { LoggerService } from '@ow/shared/services/logger.service';

const log = LoggerService.getCategory('ReviewsService');

const kPublishReviewEndpoint = 'https://storeapi.overwolf.com/apps/comment';

// ---------------------------------------------------------------------------
export class ReviewsService implements IReviewsService {
  private mapper: ReviewsMapper;
  private readonly config: any;
  private token: string;

  // ---------------------------------------------------------------------------
  constructor(config: any, loginService?: ILoginService) {
    this.config = config;
    this.mapper = new ReviewsMapper();
    this.token = null;

    if (loginService && typeof window !== 'undefined') {
      this.token = loginService.getToken();
      loginService.onTokenChanged((token) => {
        this.token = token;
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Resolve the auth token for personal / write calls. Normally it's read from
  // the `owUser` cookie in the constructor. Inside the Overwolf app the store
  // runs in a cross-origin iframe with no such cookie, so fall back to the
  // native client's signed-in user, fetched over the OverwolfStore bridge.
  private async ensureToken(): Promise<string> {
    if (this.token) {
      return this.token;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    const user = await getOverwolfCurrentUser();
    if (user?.token) {
      this.token = user.token;
    }

    return this.token;
  }

  // ---------------------------------------------------------------------------
  public async getMyReviews(appId: string): Promise<Review[]> {
    try {
      if (!appId) {
        log.error('Unable to get my reviews. Missing app id');
        return null;
      }

      const token = await this.ensureToken();
      if (!token) {
        log.error(`Unable to get my reviews ${appId}. Missing auth token`);
        return null;
      }

      const url = `${kPublishReviewEndpoint}/${encodeURIComponent(appId)}`;
      const response = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        const errorResponse = await response.text();
        log.error(
          `Failed to get my reviews ${appId}. Status: ${response.status}. Response: ${errorResponse}`
        );
        return null;
      }

      const responseText = await response.text();
      if (!responseText) {
        return [];
      }

      const parsed = JSON.parse(responseText);
      if (!parsed) {
        return [];
      }

      if (parsed?.exists === false) {
        return [];
      }

      if (Array.isArray(parsed)) {
        return parsed as Review[];
      }

      if (Array.isArray(parsed?.comments)) {
        return parsed.comments as Review[];
      }

      if (parsed?.comment) {
        return [parsed.comment as Review];
      }

      return [parsed as Review];
    } catch (error) {
      log.error(`Failed to get my reviews ${appId}. ${error?.message}`);
      return null;
    }
  }

  // ---------------------------------------------------------------------------
  public async getReviews(
    appId: string,
    page: number,
    size: number
  ): Promise<AppReviews> {
    try {
      const {  appComments } = this.config?.publicRuntimeConfig?.api;
      const internalBaseUrl =
        this.config.publicRuntimeConfig.base.InternalStoreApiBase;
      const reviewStatsUrl = `${internalBaseUrl}/${'app-review-stats-no-cache'}/${appId}`;
      const commentsUrl = `${appComments}/${appId}/${page}/${size}`;

      const promises = [
        DataFetchService.get<AppCommentsResponseDto>(commentsUrl),
        DataFetchService.get<AppRatingResponseDto>(reviewStatsUrl),
      ];

      const [commentsRes, statsRes] = await PromiseAwaiter.wait(promises) as [
        AppCommentsResponseDto,
        AppRatingResponseDto,
      ];

      if (!commentsRes || !statsRes) {
        log.error(`Unable to find reviews ${appId}`);
        return null;
      }

      const res = {
        count: statsRes.count,
        avgRating: statsRes.avgRating,
        rateDistribution: statsRes.rateDistribution,
        comments: commentsRes.comments,
      };

      return this.mapper.map({
        ...res,
      });
    } catch (e) {
      log.error(`Failed to get reviews ${appId}. ${e.message}`);
      throw new Error('Failed to get reviews', { cause: e.message });
    }
  }

  // ---------------------------------------------------------------------------
  public async getAdditionalReviews(
    appId: string,
    page: number,
    size: number
  ): Promise<Review[]> {
    const { appComments } = this.config?.publicRuntimeConfig?.api;
    const commentsUrl = `${appComments}/${appId}/${page}/${size}`;
    const res = await DataFetchService.get<AppCommentsResponseDto>(commentsUrl);
    if (!res) {
      log.error(`Unable to find reviews ${appId}`);
      return null;
    }

    return res.comments as Review[];
  }

  // ---------------------------------------------------------------------------
  public async publishReview(
    appId: string,
    rating: number,
    content: string
  ): Promise<boolean> {
    try {
      if (!appId) {
        log.error('Unable to publish review. Missing app id');
        return false;
      }

      const token = await this.ensureToken();
      if (!token) {
        log.error(`Unable to publish review ${appId}. Missing auth token`);
        return false;
      }

      const url = `${kPublishReviewEndpoint}/${encodeURIComponent(appId)}`;
      const payload = {
        content,
        rate: rating,
        // extra: this.getReviewExtra(),
      };

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorResponse = await response.text();
        log.error(
          `Failed to publish review ${appId}. Status: ${response.status}. Response: ${errorResponse}`
        );
        return false;
      }

      return true;
    } catch (error) {
      log.error(`Failed to publish review ${appId}. ${error?.message}`);
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  public async deleteReview(appId: string): Promise<boolean> {
    try {
      if (!appId) {
        log.error('Unable to delete review. Missing app id');
        return false;
      }

      const token = await this.ensureToken();
      if (!token) {
        log.error(`Unable to delete review ${appId}. Missing auth token`);
        return false;
      }

      const url = `${kPublishReviewEndpoint}/${encodeURIComponent(appId)}`;
      const response = await fetch(url, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          app_id: appId,
        }),
      });

      if (!response.ok) {
        const errorResponse = await response.text();
        log.error(
          `Failed to delete review ${appId}. Status: ${response.status}. Response: ${errorResponse}`
        );
        return false;
      }

      return true;
    } catch (error) {
      log.error(`Failed to delete review ${appId}. ${error?.message}`);
      return false;
    }
  }

  // ---------------------------------------------------------------------------
  private getReviewExtra(): Record<string, string> {
    const resolution = this.getScreenResolution();
    const screenDpi = this.getScreenDpi();

    return {
      os: this.getOsInfo(),
      mainResolution: resolution,
      mainScreenDpi: screenDpi,
      secondaryResolution: resolution,
      secondaryScreenDpi: screenDpi,
    };
  }

  // ---------------------------------------------------------------------------
  private getScreenResolution(): string {
    if (typeof window === 'undefined') {
      return '0, 0';
    }

    return `${window.screen?.width ?? 0}, ${window.screen?.height ?? 0}`;
  }

  // ---------------------------------------------------------------------------
  private getScreenDpi(): string {
    if (typeof window === 'undefined') {
      return '96';
    }

    const baseDpi = 96;
    const dpr = window.devicePixelRatio || 1;
    return `${Math.round(baseDpi * dpr)}`;
  }

  // ---------------------------------------------------------------------------
  private getOsInfo(): string {
    if (typeof navigator === 'undefined') {
      return 'Unknown 64Bit';
    }

    const userAgent = navigator.userAgent ?? '';
    const windowsMatch = /Windows NT ([0-9.]+)/i.exec(userAgent);
    const macMatch = /Mac OS X ([0-9_]+)/i.exec(userAgent);
    const androidMatch = /Android ([0-9.]+)/i.exec(userAgent);
    const iosMatch = /iPhone OS ([0-9_]+)/i.exec(userAgent);

    let osName = 'Unknown';
    let osVersion = '';

    if (windowsMatch) {
      osName = 'Windows';
      osVersion = windowsMatch[1];
    } else if (macMatch) {
      osName = 'macOS';
      osVersion = macMatch[1].replaceAll('_', '.');
    } else if (androidMatch) {
      osName = 'Android';
      osVersion = androidMatch[1];
    } else if (iosMatch) {
      osName = 'iOS';
      osVersion = iosMatch[1].replaceAll('_', '.');
    } else if (/Linux/i.test(userAgent)) {
      osName = 'Linux';
    }

    const is64Bit = /x86_64|x64|Win64|WOW64|amd64|arm64/i.test(userAgent);
    const architecture = is64Bit ? '64Bit' : '32Bit';

    return [osName, osVersion, architecture].filter(Boolean).join(' ');
  }
}
